// Pure helpers for the upload→dispatch failure path (orphan prevention) and
// for offering a recoverable orphan a "Try again" on the meeting page.
// See docs/superpowers/specs/2026-06-08-orphan-prevention-retry-design.md.

export type UploadCleanup = "mark_failed" | "mark_failed_no_audio" | "none";

/**
 * Decide what to do with a half-finished upload when handleSubmit throws.
 * - audio already in R2  -> mark_failed: keep the row, mark it errored, offer
 *   a durable "Try again" (no re-upload).
 * - row created but audio never uploaded -> mark_failed_no_audio: KEEP the
 *   row (it carries the user's typed context — the expensive part), clear the
 *   audio reference, mark it errored. The meeting page offers "Replace the
 *   audio file" instead of "Try again". Reverses the 2026-06-08
 *   rollback-delete (spec 2026-07-14 §1; DECISIONS.md 2026-07-14).
 * - no row was created -> none.
 */
export function decideUploadCleanup(args: {
  rowCreated: boolean;
  audioInR2: boolean;
}): UploadCleanup {
  if (args.audioInR2) return "mark_failed";
  if (args.rowCreated) return "mark_failed_no_audio";
  return "none";
}

/**
 * A "recoverable orphan" is a meeting whose upload finished but whose
 * processing never started: it failed (status 'error'), its audio is in R2,
 * and it has NO transcript row (the worker creates that row, so its absence
 * means Soniox never ran). A worker error that already produced a transcript
 * has a non-null transcript and must keep the destructive Danger-Zone path,
 * since resubmit would discard that real transcript.
 */
export function isRecoverableOrphan(
  meeting: { status: string; r2_object_key: string | null },
  transcript: unknown | null,
): boolean {
  return meeting.status === "error" && !!meeting.r2_object_key && transcript === null;
}
